import type { App, DirectiveBinding } from 'vue'
import { useUnifiedPermission } from '@/composables/useUnifiedPermission'

/**
 * v-permission 自定义指令 — 统一权限控制
 *
 * 用法：
 *   1. 隐藏模式（默认）：无权限 → 从 DOM 移除
 *      <el-button v-permission="'admin:role:create'">新增角色</el-button>
 *
 *   2. 禁用模式：无权限 → 可见但 disabled + tooltip
 *      <el-button v-permission:disable="'admin:role:create'">新增角色</el-button>
 */
export function setupPermissionDirective(app: App): void {
  app.directive('permission', {
    mounted(el: HTMLElement, binding: DirectiveBinding) {
      applyPermission(el, binding)
    },
    updated(el: HTMLElement, binding: DirectiveBinding) {
      applyPermission(el, binding)
    },
    unmounted(el: HTMLElement) {
      const tooltip = el.querySelector('.v-permission-tooltip')
      if (tooltip) tooltip.remove()
    },
  })
}

function applyPermission(el: HTMLElement, binding: DirectiveBinding): void {
  const { hasPermission } = useUnifiedPermission()

  const mode = binding.arg ?? 'hide'
  const code = typeof binding.value === 'string' ? binding.value : ''

  if (mode === 'disable') {
    const allowed = code ? hasPermission(code) : true
    if (!allowed) {
      ;(el as any).disabled = true
      el.style.opacity = '0.5'
      el.style.cursor = 'not-allowed'
      addTooltip(el, '需要更高权限')
      return
    }
  }

  // 默认隐藏模式
  const allowed = code ? hasPermission(code) : true
  if (!allowed) {
    el.parentNode?.removeChild(el)
  }
}

function addTooltip(el: HTMLElement, content: string): void {
  const existing = el.querySelector('.v-permission-tooltip')
  if (existing) existing.remove()

  const tip = document.createElement('span')
  tip.style.cssText = `
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    padding: 4px 8px;
    font-size: 12px;
    color: #fff;
    background: #303133;
    border-radius: 4px;
    white-space: nowrap;
    pointer-events: none;
    z-index: 3000;
    opacity: 0;
    transition: opacity 0.2s;
  `
  tip.textContent = content

  el.style.position = 'relative'
  el.addEventListener('mouseenter', () => { tip.style.opacity = '1' })
  el.addEventListener('mouseleave', () => { tip.style.opacity = '0' })

  const wrapper = document.createElement('div')
  wrapper.className = 'v-permission-tooltip'
  wrapper.appendChild(tip)
  el.appendChild(wrapper)
}
